import BN from "bignumber.js";
import { osmosis, cosmos } from "osmojs";
import Long from "long";
import StakeApiRequest from "@/common/lib/stakeApiRequest";
import ApiRequest from "@/common/lib/apiRequest";

const TWO_WEEKS_IN_SECOND = 1209600;
const RPC_ENDPOINT = process.env.VUE_APP_OSMOSIS_RPC;
const POOLS_MAP = {
  "gamm/pool/1": "ATOM / OSMO",
  "gamm/pool/678": "USDC / OSMO",
  "gamm/pool/704": "wETH / OSMO",
  "gamm/pool/712": "WBTC / OSMO",
};
const DEFAULT_LOCKS = Object.keys(POOLS_MAP).map(item => ({
  shares: 0,
  pool: {
    id: item.split("/").slice(-1)[0],
    denom: item,
    symbols: POOLS_MAP[item],
  },
  delegatedToUs: false,
  autocompounded: false,
}));
const OPERATOR = "osmo1ltd3ynqjvy39r3lc48p4zlqa59hvam5tagerlj";
const MSGS = {
  ADD_LIQUIDITY_MSG: "/osmosis.gamm.v1beta1.MsgJoinSwapExternAmountIn",
  LOCK_TOKENS_MSG: "/osmosis.lockup.MsgLockTokens",
};

const poolsInfoCache = {};

const mutationTypes = {
  SET_POOLS: "SET_POOL",
  SET_SWAP_META: "SET_SWAP_META",
  SET_ASSET_LIST: "SET_ASSET_LIST",
  SET_AUTOCOMPOUND_LOCKS: "SET_AUTOCOMPOUND_LOCKS",
  SET_GRANT_STATUS: "SET_GRANT_STATUS",
  SET_POOLS_AUTOCOMPOUND: "SET_POOLS_AUTOCOMPOUND",
};

const state = {
  pools: [],
  swapMeta: {
    routes: [],
    swapFee: 0,
    swapRate: 0,
  },
  assetList: [],
  autocompoundLocks: [],
  grantStatus: {
    granted: true,
  },
  poolsAutocompound: [],
};

const actions = {
  async fetchPools({ commit }) {
    try {
      const pools = await fetch(
        "https://api-osmosis.imperator.co/search/v1/pools"
      ).then(res => res.json());
      const poolsToUse = Object.keys(pools).map(id => ({
        id,
        pair: pools[id],
      }));
      commit(mutationTypes.SET_POOLS, poolsToUse);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchPoolInfo({}, poolId) {
    if (poolsInfoCache[poolId]) return poolsInfoCache[poolId];
    const ret = await fetch(
      `https://lcd-osmosis.keplr.app/osmosis/gamm/v1beta1/pools/${poolId}`
    )
      .then(res => res.json())
      .then(res => res.pool);
    poolsInfoCache[poolId] = ret;
    return ret;
  },
  async fetchAssetList({ commit }) {
    try {
      const res = await fetch(
        "https://raw.githubusercontent.com/osmosis-labs/assetlists/main/osmosis-1/osmosis-1.assetlist.json"
      ).then(res => res.json());
      commit(mutationTypes.SET_ASSET_LIST, res.assets);
    } catch (error) {
      console.error(error);
    }
  },
  async getSwapMeta({ commit, state, dispatch }, { offerDenom, askDenom }) {
    const allRoutes = findAllRoutes(state.pools, offerDenom, askDenom);
    const routes = findBestRoutes(allRoutes, offerDenom, askDenom);
    const poolIds = routes.map(item => item.id);
    const poolsInfo = await Promise.all(
      poolIds.map(poolId => dispatch("fetchPoolInfo", poolId))
    );
    const swapFee = calculateSwapFee(poolsInfo);
    const swapRate = calculateSwapRate(routes, offerDenom, askDenom);
    commit(mutationTypes.SET_SWAP_META, { routes, swapFee, swapRate });
  },
  async getSwapRawTx(
    { rootGetters, dispatch, state },
    { amount, tokenOutMinAmount, offerCoin, askCoin, memo, gasPriceStep }
  ) {
    await dispatch("staking/tendermint/getAccountInfo", null, { root: true });
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = rootGetters[
      "staking/tendermint/accountInfo"
    ].result;
    const { chainId } = rootGetters["staking/tendermint/blockInfo"].result;
    gasPriceStep = gasPriceStep || project.staking.gasPriceStep.medium;

    return stakeUtils.txBuilder.getRawTx("swapExactAmountIn", {
      tokenOutMinAmount: new BN(tokenOutMinAmount)
        .multipliedBy(askCoin.conversionRate)
        .toFixed(0, 1),
      routes: getTxRoutes(state.swapMeta.routes, offerCoin.base),
      tokenIn: {
        denom: offerCoin.base,
        amount: new BN(amount)
          .multipliedBy(offerCoin.conversionRate)
          .toFixed(0, 1),
      },
      denom: project.staking.denom,
      sender: wallet.address,
      chainId,
      gasPrice: new BN(project.staking.gas.SwapExactAmountIn)
        .multipliedBy(gasPriceStep)
        .toNumber(),
      gas: project.staking.gas.SwapExactAmountIn,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getGrantStatus({ rootGetters, commit }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    if (!wallet.address) return;
    const result = await StakeApiRequest.get(
      `/grants/${project.name}/${wallet.address}`
    );
    const granted = areGrantsValid(result.grants);
    commit(mutationTypes.SET_GRANT_STATUS, { granted });
  },
  async getPoolsAutocompound({ rootGetters, commit }) {
    const accountAddress = rootGetters["userWallets/walletSelected"].address;
    if (!accountAddress) return [];
    const { pools: poolsAutocompounded } = await StakeApiRequest.get(
      `/autocompound/osmosis/${accountAddress}`
    );
    commit(mutationTypes.SET_POOLS_AUTOCOMPOUND, poolsAutocompounded);
    return poolsAutocompounded;
  },
  async getAutocompoundLocks({ rootGetters, commit }) {
    const accountAddress = rootGetters["userWallets/walletSelected"].address;
    const { validatorAddr } =
      rootGetters["staking/tendermint/blockInfo"]?.result || {};

    if (!accountAddress || !validatorAddr) return DEFAULT_LOCKS;

    const { createRPCQueryClient } = osmosis.ClientFactory;
    const client = await createRPCQueryClient({ rpcEndpoint: RPC_ENDPOINT });
    const locks = await getAccountLockedDuration(client, {
      account: accountAddress,
    });
    const lockResults = await Promise.all(
      locks.map(async item => {
        const poolName = POOLS_MAP[item.coins[0].denom];
        if (!poolName) return null;
        const synthDenom = await getSyntheticLockupsByLockId(client, {
          lockId: item.lockId,
        });
        const poolId = item.coins[0].denom.split("/").slice(-1)[0];
        return {
          shares: item.coins[0].amount,
          pool: {
            id: poolId,
            denom: item.coins[0].denom,
            symbols: poolName,
          },
          delegatedToUs: synthDenom?.endsWith(validatorAddr),
          autocompounded: false,
        };
      })
    );
    const result = DEFAULT_LOCKS.filter(
      item => !lockResults.some(lock => lock?.pool.denom === item.pool.denom)
    )
      .concat(lockResults.filter(item => !!item))
      .sort((a, b) => b.shares - a.shares || a.pool.id - b.pool.id);
    commit(mutationTypes.SET_AUTOCOMPOUND_LOCKS, result);
  },
  getGrantLpAutocompoundRawTx(
    { rootGetters },
    { gas, gasPrice, memo, expiration }
  ) {
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const accountAddress = rootGetters["userWallets/walletSelected"].address;
    const { chainId } =
      rootGetters["staking/tendermint/blockInfo"]?.result || {};
    const { accountNumber, sequence } = rootGetters[
      "staking/tendermint/accountInfo"
    ].result;
    return stakeUtils.txBuilder.getRawTx("grantLpAutocompound", {
      granter: accountAddress,
      grantee: OPERATOR,
      expiration,
      feeDenom: project.staking.feeDenom || project.staking.denom,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  getRevokeLpAutocompoundRawTx({ rootGetters }, { gas, gasPrice, memo }) {
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const accountAddress = rootGetters["userWallets/walletSelected"].address;
    const { chainId } =
      rootGetters["staking/tendermint/blockInfo"]?.result || {};
    const { accountNumber, sequence } = rootGetters[
      "staking/tendermint/accountInfo"
    ].result;
    return stakeUtils.txBuilder.getRawTx("revokeLpAutocompound", {
      granter: accountAddress,
      grantee: OPERATOR,
      feeDenom: project.staking.feeDenom || project.staking.denom,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  requestAutocompoundForPools({ rootGetters }, { pools }) {
    const accountAddress = rootGetters["userWallets/walletSelected"].address;
    return ApiRequest.post(
      `/tendermint/autocompound/osmosis/${accountAddress}`,
      {
        pools,
      }
    );
  },
};

const mutations = {
  [mutationTypes.SET_POOLS](state, data) {
    state.pools = data;
  },
  [mutationTypes.SET_SWAP_META](state, data) {
    state.swapMeta = data;
  },
  [mutationTypes.SET_ASSET_LIST](state, data) {
    state.assetList = data;
  },
  [mutationTypes.SET_AUTOCOMPOUND_LOCKS](state, data) {
    state.autocompoundLocks = data;
  },
  [mutationTypes.SET_GRANT_STATUS](state, data) {
    state.grantStatus = data;
  },
  [mutationTypes.SET_POOLS_AUTOCOMPOUND](state, data) {
    state.poolsAutocompound = data;
  },
};

const getters = {
  swapableCoins(state, getters, rootState, rootGetters) {
    const accountInfo = rootGetters["staking/tendermint/accountInfo"];
    const accountBalanceMap =
      accountInfo.result?.balances?.reduce(
        (acc, cur) => ({ ...acc, [cur.denom]: cur.value }),
        {}
      ) ?? {};
    return state.assetList
      .filter(item => !!item.denom_units[1])
      .map(item => ({
        ...item,
        balance: accountBalanceMap[item.base] || 0,
        conversionRate: Math.pow(10, item.denom_units[1].exponent),
        logo: item.logo_URIs.svg || item.logo_URIs.png,
      }));
  },
  swapMeta: state => state.swapMeta,
  autocompoundLocks: state => state.autocompoundLocks,
  defaultAutocompoundLocks: () => [...DEFAULT_LOCKS],
  grantStatus: state => state.grantStatus,
  poolsAutocompound: state => state.poolsAutocompound,
};

function findAllRoutes(paths, offerDenom, askDenom) {
  const travelsalHistory = new WeakMap();
  const allRoutes = [];

  function findNodes(denom) {
    return paths
      .filter(p => !travelsalHistory.has(p) && isPairValid(p.pair, denom))
      .map(p => travelsalHistory.set(p, 1) && p);
  }

  function isPairValid(pair, denom) {
    return pair.some(item => item.denom === denom);
  }

  function traverse(nodes, offerDenom, askDenom) {
    nodes.forEach(node => {
      if (isPairValid(node.pair, askDenom)) {
        allRoutes.push(node.routes);
      } else {
        const nodeAskDenom = node.pair.find(item => item.denom !== offerDenom)
          .denom;
        const nextNodes = findNodes(nodeAskDenom).map(nextNode => ({
          ...nextNode,
          routes: [...node.routes, nextNode.id],
        }));
        traverse(nextNodes, nodeAskDenom, askDenom);
      }
    });
  }

  const initialNotes = findNodes(offerDenom).map(node => ({
    ...node,
    routes: [node.id],
  }));

  traverse(initialNotes, offerDenom, askDenom);

  const pathsMap = paths.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {});

  return allRoutes.map(routes => routes.map(nodeId => pathsMap[nodeId]));
}

function findBestRoutes(allRoutes) {
  // currently select the shortest routes, might need to choose best routes by swap rates, slippage?
  return allRoutes.sort((a, b) => a.length - b.length)[0];
}

function calculateSwapRate(routes, offerDenom) {
  return routes.reduce((acc, cur, index) => {
    const offerPrice = cur.pair.find(item => item.denom === offerDenom).price;
    const askItem = cur.pair.find(item => item.denom !== offerDenom);
    const askPrice = askItem.price;
    if (index < routes.length - 1) {
      offerDenom = routes[index + 1].pair.find(
        item => item.denom === askItem.denom
      ).denom;
    }
    return new BN(offerPrice)
      .dividedBy(askPrice)
      .multipliedBy(acc)
      .toNumber();
  }, 1);
}

function calculateSwapFee(poolsInfo) {
  return (
    100 *
    poolsInfo.reduce(
      (acc, cur) => new BN(acc).plus(cur.poolParams.swapFee).toNumber(),
      0
    )
  );
}

function getTxRoutes(routes = [], offerDenom) {
  return routes.map((node, index) => {
    const tokenOut = node.pair.find(item => item.denom !== offerDenom);
    const mapped = {
      poolId: String(node.id),
      tokenOutDenom: tokenOut.denom,
    };
    if (index < routes.length - 1) {
      offerDenom = routes[index + 1].pair.find(
        item => item.denom === tokenOut.denom
      ).denom;
    }
    return mapped;
  });
}

// autocompound locks
const getAccountLockedDuration = async (
  client,
  { account, duration = TWO_WEEKS_IN_SECOND }
) => {
  try {
    const response = await client.osmosis.lockup.accountLockedDuration({
      owner: account,
      duration: {
        seconds: Long.fromInt(duration),
        nano: 0,
      },
    });
    const locks = (response?.locks || []).map(lock => ({
      lockId: lock.ID && lock.ID.toString(),
      coins: lock.coins,
      endTime: lock.endTime,
    }));
    return locks;
  } catch (err) {
    return getAccountLockedDuration(client, { account, duration });
  }
};
const getSyntheticLockupsByLockId = async (client, { lockId }) => {
  try {
    const response = await client.osmosis.lockup.syntheticLockupsByLockupID({
      lockId: Long.fromInt(lockId),
    });
    const syntheticLock =
      response?.syntheticLocks && response?.syntheticLocks[0];
    if (!syntheticLock) {
      return null;
    }
    const endTimeYear =
      syntheticLock.endTime && syntheticLock.endTime.getUTCFullYear();
    const isBonding = endTimeYear === 1;
    if (!isBonding) {
      return null;
    }
    return syntheticLock.synthDenom;
  } catch (err) {
    return getSyntheticLockupsByLockId(client, { lockId });
  }
};

const areGrantsValid = (grants = []) => {
  const addLiquidityGrant = grants.find(
    g => g.authorization.msg === MSGS.ADD_LIQUIDITY_MSG
  );
  const lockTokenGrant = grants.find(
    g => g.authorization.msg === MSGS.LOCK_TOKENS_MSG
  );
  if (!addLiquidityGrant || !lockTokenGrant) {
    return false;
  }
  return (
    new Date(addLiquidityGrant.expiration).valueOf() > new Date().valueOf() &&
    new Date(lockTokenGrant.expiration).valueOf() > new Date().valueOf()
  );
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
